export const fixtureReceiptDate = '2026-09-27'

export interface ReceivingAsnSummary {
  id: string
  asnNumber: string
  company: string
  sender: string
  expectedDate: string
  lineCount: number
  expectedUnits: number
  shipmentReference: string
  exception?: boolean
}

export type InventoryControlCategory =
  'lot_number' | 'manufacture_date' | 'best_before_date' | 'expiry_date' | 'received_date'

export interface ReceivingVariantSummary {
  id: string
  sku: string
  barcode: string
  name: string
  description: string
  asnIds: string[]
  expectedUnits: number
  receivedUnits: number
  /** Read-only projection of product.inventory_control_category; product default is received_date. */
  inventoryControlCategory?: InventoryControlCategory
}

export interface ReceivingDimensions {
  length: number
  width: number
  height: number
  weight: number
}

export interface ReceivingPackConfiguration {
  units: number
  dimensions: ReceivingDimensions
}

export interface ReceivingPalletConfiguration extends ReceivingPackConfiguration {
  ti: number
  hi: number
}

export interface ReceivingPreprocessProfile {
  each: ReceivingDimensions
  inner: ReceivingPackConfiguration
  carton: ReceivingPackConfiguration
  pallet: ReceivingPalletConfiguration
}

export interface ReceivingFlowFixture {
  /** Deterministic warehouse receipt date. Production must stamp this server-side. */
  receiptDate?: string
  /** Fixture authorization; production must use authoritative role/policy checks. */
  shortageCompletionAllowed?: boolean
  warehouseId: string
  locations: ReceivingLocationSummary[]
  asns: ReceivingAsnSummary[]
  variants: ReceivingVariantSummary[]
  referenceMatches: Record<string, string[]>
  productMatches: Record<string, string[]>
  variantBarcodeMatches: Record<string, string>
  variantSkuMatches: Record<string, string>
  preprocessProfiles: Record<string, ReceivingPreprocessProfile>
  defaultPreprocessProfile: ReceivingPreprocessProfile
  availableLpns: string[]
}

export interface ReceivingLocationSummary {
  id: string
  barcode: string
  warehouseId: string
  active: boolean
}

const standardPreprocessProfile: ReceivingPreprocessProfile = {
  each: { length: 12, width: 9, height: 1, weight: 0.5 },
  inner: { units: 4, dimensions: { length: 12, width: 9, height: 4, weight: 2 } },
  carton: { units: 16, dimensions: { length: 19, width: 13, height: 10, weight: 9 } },
  pallet: { units: 48, ti: 3, hi: 1, dimensions: { length: 48, width: 40, height: 42, weight: 32 } },
}

export const receivingAsns: ReceivingAsnSummary[] = [
  {
    id: 'asn-24091',
    asnNumber: 'ASN-24091',
    company: 'Northwind Outfitters',
    sender: 'Northwind Distribution',
    expectedDate: 'Sep 25, 2026',
    lineCount: 1,
    expectedUnits: 64,
    shipmentReference: 'PRO-48291',
  },
  {
    id: 'asn-24092',
    asnNumber: 'ASN-24092',
    company: 'Summit Supply Co.',
    sender: 'Summit West DC',
    expectedDate: 'Sep 25, 2026',
    lineCount: 1,
    expectedUnits: 144,
    shipmentReference: 'BOL-771043',
  },
  {
    id: 'asn-24093',
    asnNumber: 'ASN-24093',
    company: 'Northwind Outfitters',
    sender: 'Northwind Distribution',
    expectedDate: 'Sep 26, 2026',
    lineCount: 1,
    expectedUnits: 48,
    shipmentReference: 'BOL-771043',
  },
]

export const receivingVariants: ReceivingVariantSummary[] = [
  {
    id: 'variant-trail-shirt',
    sku: 'NW-TS-CHAR-M',
    barcode: '00850011112222',
    name: 'Trail Shirt - Charcoal / M',
    description: "Men's performance trail shirt",
    asnIds: ['asn-24091'],
    expectedUnits: 64,
    receivedUnits: 0,
  },
  {
    id: 'variant-camp-mug',
    sku: 'SUM-MUG-12-BLK',
    barcode: '00850033334444',
    name: 'Camp Mug - 12 oz / Black',
    description: 'Insulated stainless camp mug',
    asnIds: ['asn-24092'],
    expectedUnits: 144,
    receivedUnits: 24,
  },
  {
    id: 'variant-pack-liner',
    sku: 'NW-PL-20-GRY',
    barcode: '00850055556666',
    name: 'Pack Liner - 20 L / Gray',
    description: 'Waterproof pack liner',
    asnIds: ['asn-24093'],
    expectedUnits: 48,
    receivedUnits: 0,
  },
  {
    id: 'variant-known-missing-line',
    sku: 'NW-GLV-WTR-L',
    barcode: '00850077778888',
    name: 'Winter Gloves - Large',
    description: 'Insulated waterproof gloves',
    asnIds: [],
    expectedUnits: 0,
    receivedUnits: 0,
  },
]

export const defaultReceivingFixture: ReceivingFlowFixture = {
  warehouseId: 'warehouse-01',
  locations: [
    { id: 'dock-a04', barcode: 'RCV-DOCK-A-04', warehouseId: 'warehouse-01', active: true },
    { id: 'dock-a05', barcode: 'RCV-DOCK-A-05', warehouseId: 'warehouse-01', active: true },
    { id: 'dock-closed', barcode: 'RCV-CLOSED', warehouseId: 'warehouse-01', active: false },
    { id: 'storage-a01', barcode: 'STORAGE-A-01', warehouseId: 'warehouse-01', active: true },
    { id: 'other-dock', barcode: 'RCV-OTHER-01', warehouseId: 'warehouse-02', active: true },
  ],
  asns: receivingAsns,
  variants: receivingVariants,
  referenceMatches: {
    'PRO-48291': ['asn-24091'],
    'BOL-771043': ['asn-24092', 'asn-24093'],
  },
  productMatches: {
    '00850012345678': ['asn-24092'],
    '00850087654321': ['asn-24092', 'asn-24093'],
  },
  variantBarcodeMatches: {
    '00850011112222': 'variant-trail-shirt',
    '00850033334444': 'variant-camp-mug',
    '00850055556666': 'variant-pack-liner',
    '00850077778888': 'variant-known-missing-line',
  },
  variantSkuMatches: {
    'NW-TS-CHAR-M': 'variant-trail-shirt',
    'SUM-MUG-12-BLK': 'variant-camp-mug',
    'NW-PL-20-GRY': 'variant-pack-liner',
    'NW-GLV-WTR-L': 'variant-known-missing-line',
  },
  preprocessProfiles: {
    'variant-trail-shirt': standardPreprocessProfile,
    'variant-camp-mug': {
      each: { length: 5, width: 4, height: 4, weight: 0.75 },
      inner: { units: 6, dimensions: { length: 11, width: 8, height: 6, weight: 5 } },
      carton: { units: 24, dimensions: { length: 17, width: 13, height: 13, weight: 20 } },
      pallet: { units: 96, ti: 2, hi: 2, dimensions: { length: 48, width: 40, height: 58, weight: 86 } },
    },
    'variant-pack-liner': {
      each: { length: 10, width: 8, height: 2, weight: 0.4 },
      inner: { units: 6, dimensions: { length: 12, width: 10, height: 8, weight: 3 } },
      carton: { units: 24, dimensions: { length: 22, width: 16, height: 14, weight: 12 } },
      pallet: { units: 48, ti: 2, hi: 1, dimensions: { length: 48, width: 40, height: 34, weight: 28 } },
    },
    'variant-known-missing-line': standardPreprocessProfile,
  },
  defaultPreprocessProfile: standardPreprocessProfile,
  availableLpns: Array.from({ length: 24 }, (_, index) => `LPN-RCV-${String(index + 1).padStart(3, '0')}`),
}
